/**
 * Creating things from VR: one place that builds the store item, adds it,
 * broadcasts it (which also persists it) and selects it. Used by the held
 * item (drop from your hand), duplicate, and the palette.
 *
 * Also wraps undo / redo so a time travel in VR is broadcast + saved like any
 * other edit — zundo only rolls back the local store, so without this an undo
 * in the headset came back on reload and other players never saw it.
 */
import { useWorldStore, type PrimitiveItem, type PrimitiveShape, type LightItem } from '../../../stores/worldStore';
import { broadcastObjectAdd, broadcastObjectDelete, broadcastObjectUpdate } from '../../../services/worldBroadcast';
import { redoWorld, undoWorld } from '../../../services/worldUndo';
import { getGameDef } from '../../games/registry';
import { arcadeCabinetName } from '../../games/arcade/arcadeShared';
import type { ContentPlacement } from './vrBuildState';
import { DEFAULT_SPEAKER } from '../../../services/music/musicTypes';

type V3 = [number, number, number];
const newId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

export function newPrimitiveItem(shape: PrimitiveShape, position: V3, rotY: number, extra: Partial<PrimitiveItem> = {}): PrimitiveItem {
  const isPortal = shape === 'portal';
  const isTable = shape === 'gametable';
  return {
    shape,
    name: isTable ? `${getGameDef(extra.game as string)?.label ?? 'Game'} Table` : shape === 'arcade' ? arcadeCabinetName(extra.arcadeGame) : shape === 'leaderboard' ? 'Leaderboard' : shape.charAt(0).toUpperCase() + shape.slice(1),
    position, rotation: [0, rotY, 0], scale: [1, 1, 1],
    color: isPortal ? '#8b5cf6' : isTable ? '#0f5a3c' : shape === 'speaker' ? '#22272f' : '#888888',
    texturePreset: null, textureUrl: null, pbrMaps: null, textureScale: 1,
    visible: true, isLocked: false, collidable: false, opacity: 1, metalness: 0, roughness: 0.5, colliderOnly: false,
    ...(shape === 'text' ? { text: 'Hello World', fontSize: 1 } : {}),
    ...(shape === 'speaker' ? { speaker: { ...DEFAULT_SPEAKER, tracks: [] } } : {}),
    ...extra,
  };
}

export function placePrimitive(worldId: string, item: PrimitiveItem): string {
  const id = newId('prim');
  const s = useWorldStore.getState();
  s.addPrimitive(id, item);
  if (worldId) broadcastObjectAdd(worldId, 'primitive', id, item);
  s.setSelectedObjectId(id);
  return id;
}

export function placeLight(worldId: string, position: V3, template?: LightItem): string {
  const id = newId('light');
  const item: LightItem = template
    ? { ...template, position, name: template.name?.endsWith('Copy') ? template.name : `${template.name} Copy` }
    : {
      type: 'pointlight', name: 'Point Light', position, rotation: [-Math.PI / 4, 0, 0],
      color: '#ffffff', intensity: 1, distance: 10, angle: Math.PI / 6, penumbra: .5, castShadow: true,
      visible: true, isLocked: false, width: 4, height: 4,
    };
  const s = useWorldStore.getState();
  s.addLight(id, item);
  if (worldId) broadcastObjectAdd(worldId, 'light', id, item);
  s.setSelectedObjectId(id);
  return id;
}

/** A library / Studio model (or a copy of one), standing at `position` (its base) facing `rotY`. */
export function placeContent(worldId: string, spec: ContentPlacement | null, position: V3, rotY: number, template?: Record<string, any>): string {
  const id = newId('content');
  const s = useWorldStore.getState();
  const item: any = template
    ? { ...template, position, rotation: [template.rotation?.[0] ?? 0, rotY, template.rotation?.[2] ?? 0], uploadedAt: Date.now() }
    : {
      url: spec!.url, type: 'model', position: [position[0], position[1] - (spec!.minY ?? 0), position[2]],
      scale: 1, rotation: [0, rotY, 0], visible: true, isLocked: false, filename: spec!.filename, uploadedAt: Date.now(),
      ...(spec!.autoFit ? { autoFit: true } : {}),
      ...(spec!.thumbnailUrl ? { thumbnailUrl: spec!.thumbnailUrl } : {}),
    };
  s.addContentItem(id, item);
  if (worldId) broadcastObjectAdd(worldId, 'content', id, item);
  s.setSelectedObjectId(id);
  if (!template && spec!.autoFit && Math.abs(position[1]) > .01) {
    // autoFit rests the model at y=0 once it has measured it; lift it back to
    // the height it was dropped at (a stage, a table, an upper floor…).
    const surfaceY = position[1];
    const unsub = useWorldStore.subscribe((st) => {
      const it = st.contentItems[id];
      if (!it) { unsub(); return; }
      if ((it as any).autoFit) return;
      unsub();
      const pos: V3 = [it.position[0], it.position[1] + surfaceY, it.position[2]];
      st.updateContentItem(id, { position: pos });
      if (worldId) broadcastObjectUpdate(worldId, 'content', id, { position: pos });
    });
    setTimeout(unsub, 20000);
  }
  return id;
}

// ---- undo / redo that reaches everyone (services/worldUndo) ----
export const vrUndo = (worldId: string): boolean => undoWorld(worldId);
export const vrRedo = (worldId: string): boolean => redoWorld(worldId);
